import type { PillProps } from "@validereinc/common-components";
import {
  ArtifactRefSchema,
  type ArtifactRefType,
  ApprovalDecisionMetadataSchema,
  type ApprovalDecisionType,
  InvestigationMessageKind,
  InvestigationStatus,
  TERMINAL_INVESTIGATION_STATUSES,
  type InvestigationMessageType,
  type InvestigationStatusType,
} from "@validereinc/domain";
import { z } from "zod";

/** One page from `useInfiniteQuery` for investigation messages (newest page first). */
export type InvestigationMessagesPageType = {
  data: InvestigationMessageType[];
};

/**
 * Pages come back newest-first; flatten then reverse so the feed renders oldest-first
 * (chat-style).
 */
export const flattenMessagesOldestFirst = (
  pages: InvestigationMessagesPageType[] | undefined
): InvestigationMessageType[] => {
  if (!pages?.length) return [];
  const flat = pages.flatMap((page) => page.data);
  return flat.slice().reverse();
};

export type PendingApprovalResolutionType = {
  decisionMessageId: string;
  decision: ApprovalDecisionType | null;
};

export type PendingApprovalPairingType = {
  openPendingApprovalMessage: InvestigationMessageType | null;
  resolutionsByPendingApprovalId: Map<string, PendingApprovalResolutionType>;
};

export const pairPendingApprovals = (
  messages: InvestigationMessageType[]
): PendingApprovalPairingType => {
  let openPendingApprovalMessage: InvestigationMessageType | null = null;
  let lastUnpairedPendingApprovalId: string | null = null;
  const resolutionsByPendingApprovalId = new Map<
    string,
    PendingApprovalResolutionType
  >();

  for (const message of messages) {
    if (message.kind === InvestigationMessageKind.PENDING_APPROVAL) {
      openPendingApprovalMessage = message;
      lastUnpairedPendingApprovalId = message.id;
      continue;
    }

    if (
      message.kind === InvestigationMessageKind.APPROVAL_DECISION &&
      lastUnpairedPendingApprovalId
    ) {
      const parsedDecisionMetadata = ApprovalDecisionMetadataSchema.safeParse(
        message.metadata
      );
      resolutionsByPendingApprovalId.set(lastUnpairedPendingApprovalId, {
        decisionMessageId: message.id,
        decision: parsedDecisionMetadata.success
          ? parsedDecisionMetadata.data.decision
          : null,
      });
      openPendingApprovalMessage = null;
      lastUnpairedPendingApprovalId = null;
    }
  }

  return { openPendingApprovalMessage, resolutionsByPendingApprovalId };
};

const InvestigationMessageArtifactsMetadataSchema = z.object({
  artifacts: z.array(ArtifactRefSchema),
});

/**
 * Safely parse typed message artifacts from opaque `message.metadata`.
 * Unknown or malformed payloads are treated as "no artifacts" so the chat feed
 * remains resilient to historical rows that predate this schema.
 */
export const parseMessageArtifacts = (
  metadata: InvestigationMessageType["metadata"]
): ArtifactRefType[] => {
  const parsedMetadata =
    InvestigationMessageArtifactsMetadataSchema.safeParse(metadata);
  if (!parsedMetadata.success) return [];
  return parsedMetadata.data.artifacts;
};

/** Display values for `error.recoverable` on the investigation detail error block. */
export const InvestigationRecoverable = {
  YES: "yes",
  NO: "no",
  UNKNOWN: "unknown",
} as const;
export type InvestigationRecoverableType =
  (typeof InvestigationRecoverable)[keyof typeof InvestigationRecoverable];

export const formatInvestigationRecoverable = (
  recoverable: unknown
): InvestigationRecoverableType => {
  if (recoverable === true) return InvestigationRecoverable.YES;
  if (recoverable === false) return InvestigationRecoverable.NO;
  return InvestigationRecoverable.UNKNOWN;
};

export const InvestigationStatusToPillVariantMapping: Record<
  InvestigationStatusType,
  PillProps["variant"]
> = {
  [InvestigationStatus.PENDING]: "default",
  [InvestigationStatus.RUNNING]: "info",
  [InvestigationStatus.DONE]: "success",
  [InvestigationStatus.ERROR]: "error",
};

export const InvestigationStatusLabel: Record<InvestigationStatusType, string> =
  {
    [InvestigationStatus.PENDING]: "Pending",
    [InvestigationStatus.RUNNING]: "Running",
    [InvestigationStatus.DONE]: "Done",
    [InvestigationStatus.ERROR]: "Error",
  };

const IN_PROGRESS_INVESTIGATION_STATUSES: ReadonlySet<InvestigationStatusType> =
  new Set([InvestigationStatus.PENDING, InvestigationStatus.RUNNING]);

const TERMINAL_INVESTIGATION_STATUS_SET: ReadonlySet<InvestigationStatusType> =
  new Set(TERMINAL_INVESTIGATION_STATUSES);

/**
 * Whether the investigation has finished successfully or failed. Used when
 * stopping polls and when deciding whether to run a one-shot messages refetch
 * after a busy → terminal transition.
 */
export const isTerminalInvestigationStatus = (
  status: InvestigationStatusType
): boolean => TERMINAL_INVESTIGATION_STATUS_SET.has(status);

/**
 * The detail page, follow-up form, and progress feed all need to know whether
 * the agent is mid-turn so they can disable inputs and show the live progress
 * panel. Centralized here so the create and follow-up surfaces can't drift apart.
 */
export const isAgentBusy = (status?: InvestigationStatusType): boolean =>
  status !== undefined && IN_PROGRESS_INVESTIGATION_STATUSES.has(status);
